import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  InfoTransmissionMessage,
  RestaurantStaffInterventionStatus,
  messagingActions,
} from '../../reducers/messagingSlice';
import { selectRestaurantStaffIntervention } from '../../selectors/message';
import logger from '../../utils/logger';

type InterventionReason = {
  /** Stable machine key — analytics source of truth, never changes when a label is renamed. */
  code: string;
  /** Display label shown in the popup (rendered uppercase by MUI). */
  label: string;
};

const OTHER_CODE = 'OTHER';

// Column-major layout: the first 6 fill the left column top-to-bottom, the next 6 the right.
const reasons: InterventionReason[] = [
  // Left column — item, conversation & audio problems
  { code: 'ITEM_NOT_VALIDATING', label: 'Item not validating' },
  { code: 'ITEM_MISSING', label: 'Item missing' },
  { code: 'MODIFIER_NOT_SUPPORTED', label: 'Modifier not supported' },
  { code: 'CX_QUESTION_NO_ANSWER', label: "Can't answer CX question" },
  { code: 'LANGUAGE_NOT_SUPPORTED', label: 'Language not supported' },
  { code: 'AUDIO_ISSUE', label: 'Audio issue' },
  // Right column — promo, channel, menu, staff, general catch-all & Other (last)
  { code: 'COUPON_ISSUE', label: 'Coupon issue' },
  { code: 'THIRD_PARTY_MOBILE', label: '3rd party / mobile' },
  { code: 'GREEN_BURRITO_MENU', label: 'Green Burrito menu' },
  { code: 'REQUESTED_STAFF', label: 'Requested staff' },
  { code: 'ORDER_ISSUE', label: 'Order issue' },
  { code: OTHER_CODE, label: 'Other' },
];

const NOTE_MAX_LENGTH = 120;

export default function RestaurantStaffDialog() {
  const dispatch = useDispatch();
  const restaurantStaffIntervention = useSelector(
    selectRestaurantStaffIntervention
  );

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [note, setNote] = useState('');
  // HITL-1514: the one-shot latch. Selecting a reason emits immediately and then
  // only *dispatches* the close, so MUI keeps this dialog's buttons mounted and
  // clickable for the whole exit fade (~195ms) — a bounce or a change of mind
  // inside that window used to emit a second `intervention_reason`.
  // A ref rather than state because two clicks inside one React tick (touch plus
  // the synthesized click, or two in a frame) would both read a pre-update
  // `selectedCode`; a ref mutates synchronously. It carries the code and the
  // timestamp so a suppressed click can be logged accurately even in that case.
  const emittedRef = useRef<{ code: string; at: number } | null>(null);

  // HITL-1678: UNGATED again, as it was before 7.2.0.
  //
  // HITL-1624 retired this dialog wherever issue reporting was on and moved reason
  // capture to the issue flag panel, so that one car produced one event in one lane.
  // Agents asked for it back within a day of the release. The panel is not a worse
  // idea, it is a worse MOMENT: this dialog answers the question in ONE CLICK for 11
  // of its 12 reasons, and the panel replaced that with finding a chip among 29 laid
  // out over 11 category rows and then typing at least 3 characters, all while the
  // agent is on the phone to the store.
  //
  // The cost, stated so nobody rediscovers it: supervisors read two lanes again —
  // `intervention_reason` on the websocket for staff calls, `issue_report` over REST
  // for everything else. Consolidating them was the whole point of HITL-1624 and it
  // is now back on the shelf. The five taxonomy values that ticket added are KEPT and
  // still reachable from the Issue button and the HITL-1655 gestures; only the
  // automatic filing is gone.
  //
  // Nothing about the emit changed, so the Hex dashboard needs no work: the 12 reason
  // codes and the payload shape are identical to 7.1.x.
  const isOpen =
    restaurantStaffIntervention === RestaurantStaffInterventionStatus.open;
  const isOtherSelected = selectedCode === OTHER_CODE;

  const resetLocalState = () => {
    setSelectedCode(null);
    setNote('');
  };

  // HITL-1514: re-arm on open, never on close. This dialog is mounted for the
  // life of the tab (Body/index.tsx), so nothing else ever clears the latch — and
  // clearing it in handleClose() would unlock the buttons during the exit fade,
  // which is the window the latch exists to cover. `initial -> open` is the right
  // signal: the status only returns to `initial` at a session boundary, so this
  // arms exactly once per session, which is the intended cardinality.
  useEffect(() => {
    if (!isOpen) return;
    emittedRef.current = null;
    resetLocalState();
  }, [isOpen]);

  const handleClose = () => {
    dispatch(
      messagingActions.setRestaurantStaffIntervention(
        RestaurantStaffInterventionStatus.close
      )
    );
  };

  // A reason is already on the wire, so nothing here is broken — the first one was
  // captured and transmitted correctly. But we silently dropped agent input, so:
  // warn. The two codes are what tell the two causes apart. Same code with a small
  // delta is a double-tap, and the latch is the whole fix. A *different* code,
  // especially at 300ms+, is an agent correcting themselves — which is the evidence
  // that would argue for a two-step Confirm on every reason, not just "Other".
  const logDuplicateClick = (attemptedCode: string) => {
    const emitted = emittedRef.current;
    logger.warn({
      message:
        'Intervention reason click suppressed - a reason was already sent',
      attempted_code: attemptedCode,
      emitted_code: emitted?.code ?? null,
      ms_since_emit: emitted ? Date.now() - emitted.at : null,
    });
  };

  const emitReason = (reason: InterventionReason, freeText: string | null) => {
    const message = 'Intervention reason: ' + reason.label + ' is selected';
    const payload: Partial<InfoTransmissionMessage> = {
      data: {
        message,
        type: 'METRIC',
        metadata: {
          event_name: 'intervention_reason',
          // `reason` carries the stable machine code — same field name as before
          // (backward compatible), just a stable value instead of the raw label.
          // Label/category are derivable from the code downstream, so we don't emit them.
          reason: reason.code,
          free_text: freeText,
        },
      },
    };
    // Claim the latch before emitting, and mark the choice so the agent can see
    // which reason went for the duration of the fade.
    emittedRef.current = { code: reason.code, at: Date.now() };
    setSelectedCode(reason.code);
    dispatch(messagingActions.sendInfo(payload as any));
  };

  const handleSelectReason = (reason: InterventionReason) => {
    if (emittedRef.current) {
      logDuplicateClick(reason.code);
      return;
    }
    // "Other" needs a note first — reveal the inline field instead of emitting.
    if (reason.code === OTHER_CODE) {
      setSelectedCode(OTHER_CODE);
      setNote('');
      return;
    }
    emitReason(reason, null);
    handleClose();
  };

  const handleConfirmOther = () => {
    // Reachable twice: the Confirm button and Enter in the note field. Guarding
    // only the button would leave the keyboard path open.
    if (emittedRef.current) {
      logDuplicateClick(OTHER_CODE);
      return;
    }
    const trimmed = note.trim();
    if (!trimmed) return;
    const other = reasons.find((r) => r.code === OTHER_CODE);
    if (other) emitReason(other, trimmed);
    handleClose();
  };

  return (
    <Dialog open={!!isOpen} PaperProps={{ sx: { maxWidth: '450px' } }}>
      <DialogTitle sx={{ textAlign: 'center' }}>
        Select an intervention reason
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: 'repeat(6, auto)',
            gridAutoFlow: 'column',
            gap: 1,
            mt: 0.5,
          }}
        >
          {/*
            HITL-1514: deliberately NOT `disabled` once a reason has been emitted.
            The latch in handleSelectReason is the block; a disabled MUI Button sets
            pointer-events: none, so the second click would never reach the handler
            and logDuplicateClick would never fire — silencing the one signal that
            says whether these are double-taps or agents correcting themselves. The
            contained variant is the feedback instead.
          */}
          {reasons.map((reason) => (
            <Button
              key={reason.code}
              data-testid={`reason-${reason.code}`}
              variant={selectedCode === reason.code ? 'contained' : 'outlined'}
              size="large"
              onClick={() => handleSelectReason(reason)}
            >
              {reason.label}
            </Button>
          ))}
        </Box>
        {isOtherSelected && (
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Add a note"
              placeholder="Describe the reason…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleConfirmOther();
              }}
              inputProps={{
                maxLength: NOTE_MAX_LENGTH,
                'data-testid': 'reason-other-note',
              }}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={!note.trim()}
              onClick={handleConfirmOther}
              sx={{ mt: 1.5 }}
              data-testid="reason-other-confirm"
            >
              Confirm
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
